// The question set a quiz document paints: built from the teacher's bank or an AI import, never
// bundled. Pure apart from its i18n labels, so node --test loads it directly.

import { i18n } from '../../i18n/index.ts';

export interface QuizQuestion {
  prompt: string;
  options: string[];
  /** Zero-based index into `options`, or -1 when the question carries no key (tự luận). */
  correct: number;
  /** Every key when several options are right; `correct` stays the first of them. */
  correctIndexes?: number[];
  /** Line painted beside `Câu N`; falls back to the single-choice hint. */
  hint?: string;
  /** Bank row number, so a câu chùm's children read `Câu 3.1`; unset falls back to the list position. */
  number?: number;
  /** 1-based child index inside a câu chùm. */
  part?: number;
  /** Shared passage of a câu chùm, painted above the child's prompt. */
  stem?: string;
  /** Owns a page: passages, Đúng - Sai statements and câu chùm children do not fit three to a page. */
  solo?: boolean;
  /** Đúng - Sai nhiều ý: per option, true is Đúng, false is Sai, null is unknown. */
  statementKeys?: (boolean | null)[];
  /** Prompt painted once answers are revealed; words marked with QUIZ_ANSWER_MARK paint as the answer. */
  revealPrompt?: string;
}

/** Prefix on each word of `revealPrompt` that is an answer, e.g. a filled blank. */
export const QUIZ_ANSWER_MARK = '\u0001';

/** Brackets an index into `QuizBody.images` inside any painted text: one token is one image. */
export const QUIZ_IMAGE_OPEN = '\u0002';
export const QUIZ_IMAGE_CLOSE = '\u0003';

/** A whole paintable quiz. */
export interface QuizBody {
  title: string;
  meta: string;
  questions: QuizQuestion[];
  /** Image URLs the text tokens point at; only IZ exam sets carry them. */
  images?: string[];
}

// A function, not a constant: t() at module top level would freeze the language at import time.
export function quizQuestionHint(): string {
  return i18n.t('quiz:hint.singleChoice');
}

/** `A.` .. `Z.` - a set can carry any number of options. */
export function optionLabel(index: number): string {
  return String.fromCharCode(65 + (Math.max(0, index) % 26)) + '.';
}

/* A board background is a bitmap and cannot scroll, so the board pager is the scroll: three
   questions is what the 4:3 artboard holds at a size still readable on the panel. */
export const QUIZ_QUESTIONS_PER_PAGE = 3;

/** Pages a set of `total` questions needs. At least one, so an empty set still owns a page. */
export function quizPageCount(total: number): number {
  return Math.max(1, Math.ceil(Math.max(0, total) / QUIZ_QUESTIONS_PER_PAGE));
}

/** Absolute zero-based question indexes on a one-based page, so page 2 starts at Câu 4. */
export function quizPageIndexes(page: number, total: number): number[] {
  const size = Math.max(0, total);
  const safe = Math.min(Math.max(Math.round(page), 1), quizPageCount(size));
  const start = (safe - 1) * QUIZ_QUESTIONS_PER_PAGE;
  const stop = Math.min(start + QUIZ_QUESTIONS_PER_PAGE, size);
  const indexes: number[] = [];
  for (let i = start; i < stop; i += 1) indexes.push(i);
  return indexes;
}

/** Card foot readout, e.g. `Câu 1-3/12`. */
export function quizFootLabel(page: number, total: number): string {
  const indexes = quizPageIndexes(page, total);
  // an empty set still paints a card, so the foot has to say something other than `Câu NaN`
  if (indexes.length === 0) return i18n.t('quiz:foot.empty');
  const first = indexes[0] + 1;
  const last = indexes[indexes.length - 1] + 1;
  const span = first === last ? String(first) : first + '-' + last;
  return i18n.t('quiz:foot.range', { range: span, total: Math.max(0, total) });
}

/**
 * Question indexes per page: consecutive ordinary questions pack QUIZ_QUESTIONS_PER_PAGE to a page,
 * a solo question takes one alone. A set without solo questions pages exactly like quizPageIndexes.
 */
export function quizPages(questions: QuizQuestion[]): number[][] {
  const pages: number[][] = [];
  let current: number[] = [];
  questions.forEach((question, index) => {
    if (question.solo) {
      if (current.length > 0) pages.push(current);
      pages.push([index]);
      current = [];
      return;
    }
    current.push(index);
    if (current.length === QUIZ_QUESTIONS_PER_PAGE) {
      pages.push(current);
      current = [];
    }
  });
  if (current.length > 0 || pages.length === 0) pages.push(current);
  return pages;
}

/** Indexes on a one-based page, clamped like quizPageIndexes. */
export function quizBodyPageIndexes(questions: QuizQuestion[], page: number): number[] {
  const pages = quizPages(questions);
  const safe = Math.min(Math.max(Math.round(page), 1), pages.length);
  return pages[safe - 1];
}

/** `3` or `3.1` for a câu chùm child. */
export function quizQuestionNumber(question: QuizQuestion, index: number): string {
  const number = question.number !== undefined ? question.number : index + 1;
  return question.part ? number + '.' + question.part : String(number);
}

/** Bank rows in the set: a câu chùm is one row however many children it paints. */
export function quizQuestionTotal(questions: QuizQuestion[]): number {
  let max = 0;
  let numbered = false;
  questions.forEach((question) => {
    if (question.number === undefined) return;
    numbered = true;
    if (question.number > max) max = question.number;
  });
  return numbered ? max : questions.length;
}

/** Card foot readout for a body, e.g. `Câu 1-3/12` or `Câu 4.2/6`. */
export function quizBodyFootLabel(questions: QuizQuestion[], page: number): string {
  const indexes = quizBodyPageIndexes(questions, page);
  if (indexes.length === 0) return i18n.t('quiz:foot.empty');
  const first = quizQuestionNumber(questions[indexes[0]], indexes[0]);
  const lastIndex = indexes[indexes.length - 1];
  const last = quizQuestionNumber(questions[lastIndex], lastIndex);
  return i18n.t('quiz:foot.range', { range: first === last ? first : first + '-' + last, total: quizQuestionTotal(questions) });
}
